import { API_BASE_URL } from '../config/api';

/**
 * Uploaded-asset persistence API.
 *
 * `uploadedAssets` are stored against the calling user (JWT identity) and can
 * be scoped to a video and/or character.
 */

export type UploadedAssetType = 'video' | 'audio';

export interface SaveUploadedAssetParams {
  location: string;
  type: UploadedAssetType;
  videoId?: string;
  characterId?: string;
}

export interface UploadedAsset {
  location: string;
  type: UploadedAssetType;
  videoId?: string;
  characterId?: string;
}

/** POST /uploadedAssets — persist a stored asset against the video id. */
export const saveUploadedAsset = async (
  params: SaveUploadedAssetParams,
  token: string
): Promise<UploadedAsset> => {
  const response = await fetch(`${API_BASE_URL}/uploadedAssets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error(`Failed to save uploaded asset: ${response.statusText}`);
  }

  return await response.json();
};

/** GET /uploadedAssets?videoId=… — fetch assets saved against the video id. */
export const getUploadedAssets = async (
  token: string,
  videoId?: string
): Promise<UploadedAsset[]> => {
  const query = videoId ? `?videoId=${encodeURIComponent(videoId)}` : '';
  const response = await fetch(`${API_BASE_URL}/uploadedAssets${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch uploaded assets: ${response.statusText}`);
  }

  const data: { assets?: UploadedAsset[] } = await response.json();
  return data.assets ?? [];
};